import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, ChevronDown, ChevronUp, Plus, Trash2, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { SECTION_TYPES, emptySectionData } from './CustomSection';
import { CustomSectionEditor, sectionTypeLabel } from './cms/CustomSectionEditor';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_LABELS = {
  transparency_cards: 'Why Transparency (cards)',
  featured_venues: 'Featured Venues',
  how_it_works: 'How WVC Works',
  calculator: 'Venue Calculator Showcase',
  transparency_promise: 'Transparency Promise',
  categories: 'Browse by Requirement',
  compare: 'Compare Venues',
  real_info: 'Real Venue Information',
  owners: 'For Venue Owners',
  availability_education: 'Availability Education',
  stats: 'Stats & Testimonials',
  faqs: 'FAQs',
  final_cta: 'Final CTA'
};

const F = ({ label, value, onChange, area, testId }) => (
  <div>
    <label className="text-xs font-medium text-stone-500 mb-1 block">{label}</label>
    {area
      ? <Textarea rows={3} value={value ?? ''} onChange={e => onChange(e.target.value)} data-testid={testId} />
      : <Input value={value ?? ''} onChange={e => onChange(e.target.value)} data-testid={testId} />}
  </div>
);

const HomepageCMS = ({ adminToken }) => {
  const [content, setContent] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  const [showAddSection, setShowAddSection] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fetchContent = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/homepage`);
      setContent(res.data);
    } catch {
      toast.error('Failed to load homepage content');
    }
  }, []);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  const save = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API_URL}/admin/homepage`, content, { params: { admin_token: adminToken } });
      toast.success('Homepage saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const setHero = (key, value) => setContent(p => ({ ...p, hero: { ...p.hero, [key]: value } }));
  const setSection = (idx, patch) => setContent(p => {
    const sections = [...p.sections];
    sections[idx] = { ...sections[idx], ...patch };
    return { ...p, sections };
  });
  const moveSection = (idx, dir) => setContent(p => {
    const sections = [...p.sections];
    const j = idx + dir;
    if (j < 0 || j >= sections.length) return p;
    [sections[idx], sections[j]] = [sections[j], sections[idx]];
    return { ...p, sections };
  });
  const setItem = (idx, itemIdx, patch) => {
    const items = [...(content.sections[idx].items || [])];
    items[itemIdx] = { ...items[itemIdx], ...patch };
    setSection(idx, { items });
  };

  const addCustomSection = (type) => {
    const key = `custom_${Date.now()}`;
    setContent(p => ({
      ...p,
      sections: [...p.sections, { key, type, visible: true, label: sectionTypeLabel(type), data: emptySectionData(type) }]
    }));
    setShowAddSection(false);
    setOpenKey(key);
    toast.success('Section added — fill in the content and save');
  };

  const deleteSection = (idx) => {
    if (!window.confirm('Delete this section?')) return;
    setContent(p => ({ ...p, sections: p.sections.filter((_, j) => j !== idx) }));
  };

  if (!content) return <div className="py-12 text-center"><div className="w-8 h-8 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="homepage-cms">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-serif text-lg text-stone-900">Homepage CMS</h3>
          <p className="text-xs text-stone-500">Edit every section, toggle visibility and reorder. Changes go live on save.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowAddSection(true)} data-testid="cms-add-section-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Section
          </Button>
          <a href="/" target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><ExternalLink className="w-4 h-4 mr-1" /> View Homepage</Button></a>
          <Button size="sm" onClick={save} disabled={isSaving} className="bg-stone-900 hover:bg-stone-800" data-testid="cms-save-btn">
            <Save className="w-4 h-4 mr-1" /> {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-xl p-5 space-y-4">
        <p className="font-medium text-stone-900">Hero & Search</p>
        <F label="Headline" value={content.hero?.headline} onChange={v => setHero('headline', v)} testId="cms-hero-headline-input" />
        <F label="Supporting copy" value={content.hero?.copy} onChange={v => setHero('copy', v)} area />
        <F label="Background image URL" value={content.hero?.image} onChange={v => setHero('image', v)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Search button label" value={content.hero?.search_button} onChange={v => setHero('search_button', v)} />
          <F label="Browse button label" value={content.hero?.browse_button} onChange={v => setHero('browse_button', v)} />
        </div>
        <F label="Browse tab supporting line" value={content.hero?.browse_line} onChange={v => setHero('browse_line', v)} />
        <F label="Trust points (one per line)" value={(content.hero?.trust_points || []).join('\n')} onChange={v => setHero('trust_points', v.split('\n').filter(Boolean))} area />
        <div className="grid grid-cols-2 gap-4">
          <F label="Calendar fresh within (days)" value={String(content.staleness?.current_days ?? 7)} onChange={v => setContent(p => ({ ...p, staleness: { ...p.staleness, current_days: Number(v) || 7 } }))} />
          <F label="Verify threshold (days)" value={String(content.staleness?.verify_days ?? 14)} onChange={v => setContent(p => ({ ...p, staleness: { ...p.staleness, verify_days: Number(v) || 14 } }))} />
        </div>
      </div>

      {content.sections.map((s, idx) => (
        <div key={s.key} className="bg-white border border-stone-200 rounded-xl overflow-hidden" data-testid={`cms-section-${s.key}`}>
          <div className="px-5 py-3.5 flex items-center justify-between gap-3">
            <button onClick={() => setOpenKey(openKey === s.key ? null : s.key)} className="flex items-center gap-2 text-left flex-1">
              <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${openKey === s.key ? 'rotate-180' : ''}`} />
              <span className="font-medium text-stone-900 text-sm">{s.type ? (s.label || sectionTypeLabel(s.type)) : (SECTION_LABELS[s.key] || s.key)}</span>
              {s.type && <span className="text-[10px] uppercase tracking-wider bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full">Custom · {sectionTypeLabel(s.type)}</span>}
            </button>
            <div className="flex items-center gap-2">
              <button onClick={() => moveSection(idx, -1)} className="p-1 hover:bg-stone-100 rounded" data-testid={`cms-up-${s.key}`}><ChevronUp className="w-4 h-4 text-stone-500" /></button>
              <button onClick={() => moveSection(idx, 1)} className="p-1 hover:bg-stone-100 rounded" data-testid={`cms-down-${s.key}`}><ChevronDown className="w-4 h-4 text-stone-500" /></button>
              {s.type && (
                <button onClick={() => deleteSection(idx)} className="p-1 hover:bg-red-50 rounded" data-testid={`cms-delete-${s.key}`}><Trash2 className="w-4 h-4 text-red-400" /></button>
              )}
              <Switch checked={s.visible !== false} onCheckedChange={v => setSection(idx, { visible: v })} data-testid={`cms-toggle-${s.key}`} />
            </div>
          </div>
          {openKey === s.key && s.type && (
            <div className="px-5 pb-5 space-y-4 border-t border-stone-100 pt-4">
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Internal label</label>
                <Input value={s.label ?? ''} onChange={e => setSection(idx, { label: e.target.value })} />
              </div>
              <CustomSectionEditor type={s.type} data={s.data || {}} onChange={data => setSection(idx, { data })} />
            </div>
          )}
          {openKey === s.key && !s.type && (
            <div className="px-5 pb-5 space-y-4 border-t border-stone-100 pt-4">
              {'heading' in s && <F label="Heading" value={s.heading} onChange={v => setSection(idx, { heading: v })} />}
              {'copy' in s && <F label="Supporting copy" value={s.copy} onChange={v => setSection(idx, { copy: v })} area />}
              {'tagline' in s && <F label="Tagline" value={s.tagline} onChange={v => setSection(idx, { tagline: v })} />}
              {'cta_label' in s && (
                <div className="grid grid-cols-2 gap-4">
                  <F label="CTA label" value={s.cta_label} onChange={v => setSection(idx, { cta_label: v })} />
                  <F label="CTA URL" value={s.cta_url} onChange={v => setSection(idx, { cta_url: v })} />
                </div>
              )}
              {'note' in s && <F label="Note" value={s.note} onChange={v => setSection(idx, { note: v })} />}
              {'mode' in s && (
                <div>
                  <label className="text-xs font-medium text-stone-500 mb-1 block">Compare mode</label>
                  <select value={s.mode} onChange={e => setSection(idx, { mode: e.target.value })} className="border border-stone-200 rounded-lg px-3 py-2 text-sm" data-testid="cms-compare-mode">
                    <option value="active">Active</option>
                    <option value="coming_soon">Coming Soon</option>
                    <option value="hidden">Hidden</option>
                  </select>
                </div>
              )}
              {s.settings && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <F label="Venues shown" value={String(s.settings.count ?? 6)} onChange={v => setSection(idx, { settings: { ...s.settings, count: Number(v) || 6 } })} />
                  {['show_pricing', 'show_capacity', 'show_availability'].map(k => (
                    <div key={k} className="flex items-center gap-2 pt-5">
                      <Switch checked={s.settings[k] !== false} onCheckedChange={v => setSection(idx, { settings: { ...s.settings, [k]: v } })} />
                      <span className="text-xs text-stone-600">{k.replace('show_', 'Show ')}</span>
                    </div>
                  ))}
                </div>
              )}
              {s.stats && (
                <div className="space-y-2">
                  <label className="text-xs font-medium text-stone-500 block">Stats (only active ones display)</label>
                  {s.stats.map((st, si) => (
                    <div key={si} className="flex gap-2 items-center">
                      <Input className="flex-1" value={st.label} placeholder="Label" onChange={e => { const stats = [...s.stats]; stats[si] = { ...st, label: e.target.value }; setSection(idx, { stats }); }} />
                      <Input className="flex-1" value={st.value} placeholder="Value" onChange={e => { const stats = [...s.stats]; stats[si] = { ...st, value: e.target.value }; setSection(idx, { stats }); }} />
                      <Switch checked={!!st.active} onCheckedChange={v => { const stats = [...s.stats]; stats[si] = { ...st, active: v }; setSection(idx, { stats }); }} />
                      <button onClick={() => setSection(idx, { stats: s.stats.filter((_, j) => j !== si) })}><Trash2 className="w-4 h-4 text-red-400" /></button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setSection(idx, { stats: [...s.stats, { label: '', value: '', active: false }] })}><Plus className="w-4 h-4 mr-1" /> Add stat</Button>
                </div>
              )}
              {Array.isArray(s.items) && (
                <div className="space-y-3">
                  <label className="text-xs font-medium text-stone-500 block">Items / Cards</label>
                  {s.items.map((item, ii) => (
                    <div key={ii} className="border border-stone-200 rounded-lg p-3 space-y-2 relative">
                      <button onClick={() => setSection(idx, { items: s.items.filter((_, j) => j !== ii) })} className="absolute top-2 right-2"><Trash2 className="w-4 h-4 text-red-400" /></button>
                      {'q' in item
                        ? <>
                            <F label="Question" value={item.q} onChange={v => setItem(idx, ii, { q: v })} />
                            <F label="Answer" value={item.a} onChange={v => setItem(idx, ii, { a: v })} area />
                          </>
                        : <>
                            <F label="Title" value={item.title} onChange={v => setItem(idx, ii, { title: v })} />
                            {'description' in item && <F label="Description" value={item.description} onChange={v => setItem(idx, ii, { description: v })} area />}
                            {'image' in item && <F label="Image URL" value={item.image} onChange={v => setItem(idx, ii, { image: v })} />}
                            {'url' in item && <F label="Link URL" value={item.url} onChange={v => setItem(idx, ii, { url: v })} />}
                          </>}
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setSection(idx, { items: [...s.items, s.key === 'faqs' ? { q: '', a: '' } : s.key === 'categories' ? { title: '', image: '', url: '/venues' } : { title: '', description: '' }] })}>
                    <Plus className="w-4 h-4 mr-1" /> Add item
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      <div className="flex justify-end">
        <Button onClick={save} disabled={isSaving} className="bg-stone-900 hover:bg-stone-800" data-testid="cms-save-btn-bottom">
          <Save className="w-4 h-4 mr-1" /> {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <Dialog open={showAddSection} onOpenChange={setShowAddSection}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Add a Section</DialogTitle>
            <DialogDescription>Choose a section type. It will be added at the bottom — use the arrows to move it anywhere.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {SECTION_TYPES.map(t => (
              <button
                key={t.type}
                onClick={() => addCustomSection(t.type)}
                className="border border-stone-200 rounded-lg px-3 py-3 text-sm text-left hover:border-stone-900 hover:bg-stone-50 transition-colors"
                data-testid={`add-section-${t.type}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomepageCMS;
